import { MobXProviderContext } from 'mobx-react';
import { ContactModelBase, IContactBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useUIStore } from 'Modules/ui/index.store';
import React from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumberNoPlusIfUS } from 'Utils/phoneUtil';

type UseCallLogSidebarInfoParams = {
  personId?: number;
  phoneNumber?: string;
  overrideContact?: ContactModelV2;
  onCloseMenu?: () => void;
};

/**
 * Hook to resolve and display the correct contact info in the entity side panel
 * when a call log row action is triggered. Handles three cases:
 * - An override contact (from a saved contact search)
 * - An unresolved phone number
 * - A known person by id
 *
 * @param personId - The person id
 * @param phoneNumber - The phone number
 * @param overrideContact - When provided, bypasses contact resolution and uses this contact directly
 * @param onCloseMenu - Optional callback to close the action menu after opening the side panel
 */
export const useCallLogSidebarInfo = ({
  personId,
  phoneNumber,
  overrideContact,
  onCloseMenu,
}: UseCallLogSidebarInfoParams) => {
  const {
    conversation: { loadOrCreateConversationWithPost, setChannelInfoDetails },
    person: { loadPersonByIdGetIfMissingGet, getAllContactsWithSameNum },
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const { setEntitySidePanelInfo, resetEntitySidePanel } = useUIStore();

  const handleSidebarInfoForContact = (contact: ContactModelV2) => {
    setEntitySidePanelInfo({
      contact,
      isLoading: false,
    });
  };

  const handleSidebarInfoForNumber = async () => {
    setEntitySidePanelInfo({
      isLoading: true,
    });

    const formatedPhoneNum = formatNumberNoPlusIfUS(phoneNumber);

    const conversation = await loadOrCreateConversationWithPost(
      null,
      formatedPhoneNum
    );

    let phoneNum = conversation.data.participants[0].phone;

    const contactsPbo = getAllContactsWithSameNum(phoneNumber);

    contactsPbo.case({
      fulfilled: (contacts) => {
        const contact = contacts[0];

        phoneNum =
          contact?.phoneNumbers[0]?.normalizedNumber?.replace(/\D/g, '') ||
          phoneNum ||
          phoneNumber;

        setEntitySidePanelInfo({
          contact:
            contact ??
            new ContactModelBase({
              phoneNumber: phoneNum,
            } as IContactBase),
          isLoading: false,
        });
      },
      rejected: () => {
        resetEntitySidePanel();
      },
    });
  };

  const handleSidebarInfoForPerson = async () => {
    setEntitySidePanelInfo({
      isLoading: true,
    });

    const person = await loadPersonByIdGetIfMissingGet(personId.toString());

    setEntitySidePanelInfo({
      contact: person,
      isLoading: false,
    });
  };

  const openInfoDetails = async () => {
    if (overrideContact) {
      handleSidebarInfoForContact(overrideContact);
    } else if (!personId) {
      await handleSidebarInfoForNumber();
    } else {
      await handleSidebarInfoForPerson();
    }
    setChannelInfoDetails('0', 'new');
    onCloseMenu?.();
  };

  return { openInfoDetails };
};
